import React, { useEffect, useRef, useState } from 'react';
import { createAddress } from 'services/api/campaign';
import { Loader } from '@googlemaps/js-api-loader';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useParams } from 'react-router-dom';
type Address = {
    place_id?: string;
    name?: string;
    formatted?: string;
    lat?: number;
    lng?: number;
    sublocality?: string;
    premise?: string;
    city?: string;
    region?: string;
    pincode?: string;
    business_hours?: string | null;
    phone?: string | null;
};

interface Props {
    campaignId: string;
    onAddressStored?: (data: {name: string, address: string, phone: string, business_hours: string}) => void;
}

const AddressForm: React.FC<Props> = ({
    campaignId,
    onAddressStored,
}) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
    const [address, setAddress] = useState<Address>({});
    const [addressUuid, setAddressUuid] = useState<string | null>(null);

    const center = { lat: 21.0, lng: 78.0 };

    const makeAddress = [
        address.premise,
        address.sublocality,
        address.city,
        address.region,
        address.pincode,
    ]
        .filter(Boolean)
        .join(', ');

    useEffect(() => {
        const initMap = async () => {
            const loader = new Loader({
                apiKey: 'AIzaSyARbYQLEKqJAag3lLkliWIfEwAjKerMjnM',
                version: 'weekly',
                libraries: ['places', 'marker'],
            });
            await loader.load();
            const mapsLib = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
            const markerLib = (await google.maps.importLibrary('marker')) as typeof google.maps.marker;

            const { Map } = mapsLib;
            const { AdvancedMarkerElement } = markerLib;

            const gMap = new Map(mapRef.current!, {
                center,
                zoom: 5,
                mapId: '4504f8b37365c3d0',
                mapTypeControl: false,
            });

            setMap(gMap);

            const placeAutocomplete = new (window as any).google.maps.places.PlaceAutocompleteElement({
                includedRegionCodes: ['in'],
                includedPrimaryTypes: ['establishment'],
            } as any);

            placeAutocomplete.id = 'place-autocomplete-input';
            placeAutocomplete.locationBias = center;

            cardRef.current?.appendChild(placeAutocomplete);
            gMap.controls[google.maps.ControlPosition.TOP_LEFT].push(cardRef.current!);

            const gMarker = new AdvancedMarkerElement({ map: gMap });
            setMarker(gMarker);

            placeAutocomplete.addEventListener('gmp-select', async ({ placePrediction }: any) => {
                const place = placePrediction.toPlace();
                await place.fetchFields({
                    fields: [
                        'displayName',
                        'formattedAddress',
                        'location',
                        'viewport',
                        'addressComponents',
                        'internationalPhoneNumber',
                        'regularOpeningHours',
                    ],
                });

                if (place.viewport) {
                    gMap.fitBounds(place.viewport);
                } else {
                    gMap.setCenter(place.location);
                    gMap.setZoom(17);
                }

                const newAddress: Address = {
                    place_id: place.id,
                    name: place.displayName,
                    formatted: place.formattedAddress,
                    lat: place.location.lat(),
                    lng: place.location.lng(),
                    premise: '',
                    sublocality: '',
                };

                const premiseParts = new Set<string>();
                const sublocalityParts = new Set<string>();
                const nameParts = new Set<string>([place.displayName]);

                place.addressComponents.forEach((component: any) => {
                    const types = component.types;
                    if (types.includes('subpremise') || types.includes('premise')) {
                        premiseParts.add(component.longText);
                    }
                    if (types.includes('route') || types.includes('street_number') || types.includes('sublocality')) {
                        sublocalityParts.add(component.longText);
                    }
                    if (types.includes('sublocality_level_1')) {
                        sublocalityParts.add(component.longText);
                        nameParts.add(component.longText);
                    }
                    if (types.includes('locality')) {
                        nameParts.add(component.longText);
                        newAddress.city = component.longText;
                    }
                    if (types.includes('administrative_area_level_1')) {
                        newAddress.region = component.longText;
                    }
                    if (types.includes('postal_code')) {
                        newAddress.pincode = component.longText;
                    }
                });

                newAddress.premise = Array.from(premiseParts).join(', ');
                newAddress.sublocality = Array.from(sublocalityParts).join(', ');
                newAddress.name = Array.from(nameParts).join(', ');
                newAddress.business_hours = place.regularOpeningHours?.weekdayDescriptions
                    ? summarizeOpeningHours(place.regularOpeningHours.weekdayDescriptions)
                    : null;
                newAddress.phone = place.internationalPhoneNumber?.replace(/\s+/g, '') ?? null;

                setAddress(newAddress);
                gMarker.position = place.location;
            });
        };

        initMap();
    }, []);

    const summarizeOpeningHours = (weekdayDescriptions: string[]): string => {
        const dayAbbr = {
            Monday: 'Mon',
            Tuesday: 'Tue',
            Wednesday: 'Wed',
            Thursday: 'Thu',
            Friday: 'Fri',
            Saturday: 'Sat',
            Sunday: 'Sun',
        };

        const dayHours = weekdayDescriptions.map((desc) => {
            const [day, hours] = desc.split(': ');
            return { day, hours };
        });

        const groups: { startDay: string; endDay: string; hours: string }[] = [];
        let current = {
            startDay: dayHours[0].day,
            endDay: dayHours[0].day,
            hours: dayHours[0].hours
        };

        for (let i = 1; i < dayHours.length; i++) {
            const { day, hours } = dayHours[i];
            if (hours === current.hours) {
                current.endDay = day;
            } else {
                groups.push({ ...current });
                current = { startDay: day, endDay: day, hours };
            }
        }
        groups.push({ ...current });

        return groups
            .map(({ startDay, endDay, hours }) => {
                const start = dayAbbr[startDay as keyof typeof dayAbbr];
                const end = dayAbbr[endDay as keyof typeof dayAbbr];
                return `${start}${start === end ? '' : `–${end}`}: ${hours}`;
            })
            .join('; ');
    };

    const storeAddress = async () => {
        const payload = {
            campaign_id: campaignId,
            ...address,
        };


        try {
            const storedAddress = await createAddress(payload);
            if(storedAddress){
                setAddressUuid(storedAddress.uuid);
                onAddressStored?.({name: address.name || '', address: storedAddress.uuid, business_hours: address.business_hours || '', phone: address.phone || ''});
            }
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <div
                id="place-autocomplete-card"
                ref={cardRef}
                style={{
                    marginTop: '10px',
                    marginLeft: '10px',
                    zIndex: 5,
                    background: '#fff',
                    padding: '5px',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                    borderRadius: '0.5rem',
                }}
            />
            <div
                ref={mapRef}
                style={{
                    width: '100%',
                    height: '400px',
                    position: 'relative',
                    borderRadius: '0.5rem',
                }}
            />
            {address.name && (
                <div
                    className="card"
                    style={{
                        position: 'absolute',
                        bottom: 10,
                        left: 10,
                        zIndex: 10,
                        background: '#fff',
                        borderRadius: '0.5rem',
                        padding: '10px',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                        maxWidth: '300px',
                    }}
                >
                    <p style={{ margin: '0 0 8px' }}>{makeAddress}</p>
                    <button
                        onClick={storeAddress}
                        style={{
                            padding: '4px 10px',
                            fontSize: '14px',
                            border: 'none',
                            borderRadius: '4px',
                            background: '#0d6efd',
                            color: '#fff',
                            cursor: 'pointer',
                        }}
                    >
                        Save Address
                    </button>
                </div>
            )}
        </div>
    );
};

export default AddressForm;